import * as R from 'ramda'
import clsx from 'clsx'
import { resizeAvatar_250 } from 'commons/resizeImage'
import apiConfigs from 'app/configs/apiConfigs'
import { CircleCheck, ImageIcon, Star, Trash2 } from 'lucide-react'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import { useIntl } from 'react-intl'
import { DeleteOutlinedIcon } from 'app/components/icons'

const IMAGE_KIT = apiConfigs.cndUrl

const ProgressBar = ({ percentage }) => (
  <div className="absolute bottom-[10px] sm:bottom-[12px] left-[10px] sm:left-[12px] right-[10px] sm:right-[12px] h-[10px] bg-[rgba(6,78,117,0.19)] rounded-[6px] z-[2]">
    <div
      className="h-full bg-[#00B2AD] rounded-[6px] transition-all duration-300"
      style={{ width: `${percentage}%` }}
    />
  </div>
)

const LoadingOverlay = () => (
  <div className="absolute inset-0 bg-white opacity-80 rounded-5 z-[1]" />
)

export default (props) => {
  const {
    image,
    isPreview = false,
    handleDeleteImage,
    handleSelectImage,
    imageClassName,
    className,
    displayLimit,
    showFeatured = false,
    disableSelect = false,
  } = props

  const { formatMessage } = useIntl()

  const isLoading = R.propOr(false, 'isLoading', image)
  const percentage = R.propOr(0, 'percentage', image)
  const imageUrl = R.cond([
    [
      R.compose((url) => url?.startsWith('https'), R.prop('url')),
      R.prop('url'),
    ],
    [
      R.T,
      R.compose(
        (url) => `${IMAGE_KIT}/${resizeAvatar_250(url)}`,
        R.prop('url'),
      ),
    ],
  ])(image)

  const hasUrl = !!R.prop('url', image)

  return (
    <div
      className={clsx(
        'relative flex flex-col gap-1.5 sm:gap-2 p-3 border border-1 border-neutral-200 rounded-[8px] sm:p-4 md:p-6 bg-white group flex-shrink-0',
        isPreview && 'w-[143px]',
        !isPreview && 'w-[165px]',
        displayLimit &&
          !isPreview &&
          `sm:w-[${100 / (displayLimit + 1) - 0.5}%]`,
        className,
      )}
    >
      <div className="relative">
        {hasUrl ? (
          <img
            src={imageUrl}
            alt={R.prop('name', image)}
            className={clsx(
              'w-full h-[105px] aspect-square object-cover border border-1 rounded-t-[8px] border-neutral-200 object-cover rounded-5',
              imageClassName,
              isPreview && 'h-[83px]',
            )}
          />
        ) : (
          <div
            className={clsx(
              'w-full h-[105px] aspect-square border border-1 rounded-t-[8px] border-neutral-200 rounded-5 bg-neutral-100 flex flex-col items-center justify-center',
              isPreview && 'h-[83px]',
            )}
          >
            <ImageIcon size={32} className="w-8 h-8 text-neutral-400 mb-2" />
            {isLoading && (
              <div className="w-full px-2">
                <ProgressBar percentage={percentage} />
              </div>
            )}
          </div>
        )}

        {isLoading && hasUrl && (
          <>
            <LoadingOverlay />
            <ProgressBar percentage={percentage} />
          </>
        )}
      </div>
      {R.allPass([R.prop('isSelected'), () => !isPreview || showFeatured])(
        image,
      ) && (
        <div className="absolute top-4 sm:top-5 left-4 sm:left-5 h-[20px] sm:h-[24px] md:h-[28px] w-fit rounded-full bg-white flex items-center justify-center gap-0.5 px-4 sm:px-6 text-secondary">
          <CircleCheck
            className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white flex-shrink-0"
            fill="#00B2AD"
          />
          <span className="text-[10px] mr-1 font-medium">
            {formatMessage({ id: 'compose.dialog.attachments.featured' })}
          </span>
        </div>
      )}
      <p
        className={clsx(
          'text-black flex gap-1 sm:gap-2',
          isPreview ? 'items-center' : 'overflow-hidden',
        )}
      >
        <ImageIcon
          size={14}
          className={clsx(
            'flex-shrink-0 w-[16px] h-[16px] sm:w-[16px] sm:h-[16px] mt-0.5 sm:mt-0',
          )}
        />
        <div
          className={clsx(
            'text-[14px]',
            isPreview
              ? 'truncate w-0 flex-1 min-w-0'
              : 'truncate overflow-hidden',
          )}
        >
          {R.prop('name', image)}
        </div>
      </p>

      {!isPreview && !isLoading && (
        <div className="absolute inset-0 bg-black/50 rounded-5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2 sm:gap-3 md:gap-4 z-[3]">
          {!disableSelect && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleSelectImage(image)
              }}
              className="w-[40px] h-[40px] p-[4px] flex items-center justify-center rounded-5 transition-colors bg-white/90 text-primary hover:bg-white"
              type="button"
            >
              <PrimaryTooltip
                title={formatMessage({
                  id: `compose.dialog.attachments.featuredImageTooltip.${
                    R.prop('isSelected', image) ? 'remove' : 'select'
                  }`,
                })}
              >
                <Star
                  className="w-[24px] h-[24px] flex-shrink-0"
                  fill={R.ifElse(
                    R.prop('isSelected'),
                    R.always('currentColor'),
                    R.always('none'),
                  )(image)}
                />
              </PrimaryTooltip>
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteImage(image)
            }}
            className="w-[40px] h-[40px] p-[4px] flex items-center justify-center bg-white/90 text-red-500 rounded-5 hover:bg-white transition-colors"
            type="button"
            title="Delete"
          >
            <DeleteOutlinedIcon classes="w-[24px] h-[24px] flex-shrink-0" />
          </button>
        </div>
      )}
    </div>
  )
}
